import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface BreakdownBarListRow {
  key: string;
  label: ReactNode;
  primary: number;
  secondary?: number;
  secondaryLabel?: string;
}

interface BreakdownBarListProps {
  rows: BreakdownBarListRow[];
  primaryLabel?: string;
  /** When true, the primary count row text is suffixed with primaryLabel. */
  showPrimaryLabel?: boolean;
}

/**
 * Flat bar list in canonical (given) order — never sorted by count.
 * Bars are sized on the primary count; zero-count rows are amber-highlighted
 * so catalogue gaps stand out.
 */
export function BreakdownBarList({
  rows,
  primaryLabel = 'Activities',
  showPrimaryLabel = false,
}: BreakdownBarListProps) {
  const maxPrimary = Math.max(0, ...rows.map((row) => row.primary));

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const isZero = row.primary === 0;
        const widthPercent = maxPrimary > 0 ? (row.primary / maxPrimary) * 100 : 0;
        const tooltip = `${row.primary} ${primaryLabel.toLowerCase()}${
          row.secondary !== undefined
            ? ` · ${row.secondary} ${row.secondaryLabel ?? 'active stages'}`
            : ''
        }`;

        return (
          <div
            key={row.key}
            title={tooltip}
            className={cn(
              'flex items-center gap-3 rounded-pine-input border px-3 py-2',
              isZero ? 'bg-amber-50 border-amber-300' : 'border-pine-line bg-white'
            )}
          >
            <div
              className={cn(
                'min-w-0 flex-1 text-sm',
                isZero ? 'text-amber-800 font-medium' : 'text-pine-ink'
              )}
            >
              {row.label}
            </div>
            <div className="w-40 shrink-0 hidden sm:block">
              {!isZero && (
                <div
                  className="h-2 rounded-full bg-pine-pine"
                  style={{ width: `${widthPercent}%` }}
                />
              )}
            </div>
            <div
              className={cn(
                'shrink-0 text-right text-sm tabular-nums',
                isZero ? 'text-amber-800 font-semibold' : 'text-pine-ink'
              )}
            >
              <span className="font-semibold">{row.primary}</span>
              {showPrimaryLabel && (
                <span className={cn('ml-1 text-xs', isZero ? 'text-amber-700' : 'text-pine-ink-soft')}>
                  {primaryLabel.toLowerCase()}
                </span>
              )}
              {row.secondary !== undefined && (
                <span className={cn('ml-2 text-xs', isZero ? 'text-amber-700' : 'text-pine-ink-soft')}>
                  {row.secondary} {row.secondaryLabel ?? 'active stages'}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
